import React, { useEffect, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { Dialog, DialogContent, DialogTitle } from '../Dialog/Dialog';
import HomepageAdsAPI, { LiveHomepageAd } from '../../api/HomepageAdsAPI';
import { sanitizeHomepageAdHtml } from '../../utils/homepageAdHtmlSanitize';
import { useCrispChat } from '../Crisp/CrispProvider';
import { ONBOARDING_BRAND } from '../OnboardingFlow/onboardingBrand';
import PrimaryButton from '../PrimaryButton/PrimaryButton';
import { cn } from '../../lib/utils';
import './HomeAdModal.css';

interface HomeAdModalProps {
  ad: LiveHomepageAd | null;
  onClose: () => void;
}

const BODY_CLASSES = `${ONBOARDING_BRAND.scope} homepage-ad-body text-[15px] leading-relaxed text-muted-foreground [&_a]:font-medium [&_a]:text-foreground [&_a]:underline [&_a]:underline-offset-2 [&_b]:font-semibold [&_b]:text-foreground [&_em]:italic [&_h1]:mb-2 [&_h1]:mt-5 [&_h1]:text-lg [&_h1]:font-semibold [&_h1]:leading-snug [&_h1]:text-foreground [&_h1:first-child]:mt-0 [&_h2]:mb-2 [&_h2]:mt-5 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:leading-snug [&_h2]:text-foreground [&_h2:first-child]:mt-0 [&_h3]:mb-2 [&_h3]:mt-4 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:leading-snug [&_h3]:text-foreground [&_h3:first-child]:mt-0 [&_i]:italic [&_li]:leading-relaxed [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:space-y-1.5 [&_ol]:pl-5 [&_p]:mb-4 [&_p:last-child]:mb-0 [&_strong]:font-semibold [&_strong]:text-foreground`;

const HomeAdModal: React.FC<HomeAdModalProps> = ({ ad, onClose }) => {
  const { openChat } = useCrispChat();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!ad) {
      setIsSubmitting(false);
      return;
    }
    datadogRum.addAction('Homepage Ad - Opened', { adId: ad.id, campaignName: ad.name });
    void HomepageAdsAPI.recordEngagement(ad.id).catch((error) => {
      console.error('Failed to record homepage ad engagement', { adId: ad.id, error });
    });
  }, [ad]);

  if (!ad) {
    return null;
  }

  const handleOpenChat = async () => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await HomepageAdsAPI.registerInterest(ad.id);
      datadogRum.addAction('Homepage Ad - Chat Clicked', {
        adId: ad.id,
        campaignName: ad.name,
        alreadyInterested: response.alreadyInterested,
      });
    } catch (error) {
      console.error('Failed to register homepage ad interest', { adId: ad.id, error });
    }
    onClose();
    openChat();
  };

  const heroUrl = ad.imageUrl?.trim() ?? '';
  const kicker = ad.title?.trim() ?? '';
  const showKicker = Boolean(kicker) && kicker !== ad.modalTitle;
  const heroAlt = kicker || ad.modalTitle;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent
        className={cn(
          'flex w-[calc(100%-1.5rem)] max-w-2xl flex-col gap-0 overflow-hidden rounded-2xl border-0 p-0 shadow-2xl sm:max-w-2xl sm:rounded-2xl',
          'max-h-[90dvh]',
          '[&>button:last-child]:right-3 [&>button:last-child]:top-3 [&>button:last-child]:z-20',
          '[&>button:last-child]:rounded-full [&>button:last-child]:bg-white/90 [&>button:last-child]:p-1.5',
          '[&>button:last-child]:text-foreground [&>button:last-child]:opacity-100 [&>button:last-child]:shadow-sm',
          '[&>button:last-child]:hover:bg-white [&>button:last-child]:hover:opacity-100'
        )}
      >
        {heroUrl ? (
          <div className="homepage-ad-hero relative h-40 shrink-0 overflow-hidden md:h-52">
            <img
              src={heroUrl}
              alt={heroAlt}
              className="homepage-ad-hero-img absolute inset-0 h-full w-full object-cover"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent" />
          </div>
        ) : null}

        <div className="flex min-h-0 flex-1 flex-col bg-background">
          <div className={cn('min-h-0 flex-1 overflow-y-auto px-6 pb-2', heroUrl ? 'pt-5' : 'pt-6 pr-12')}>
            {showKicker ? (
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-darker-gold-600">
                {kicker}
              </p>
            ) : null}
            <DialogTitle className="text-xl font-semibold leading-snug tracking-tight text-foreground md:text-2xl">
              {ad.modalTitle}
            </DialogTitle>
            <div
              className={`${BODY_CLASSES} mt-4`}
              dangerouslySetInnerHTML={{ __html: sanitizeHomepageAdHtml(ad.modalBody) }}
            />
          </div>

          <div className="relative shrink-0 px-6 pb-6 pt-4">
            <div className="pointer-events-none absolute inset-x-0 -top-6 h-6 bg-gradient-to-t from-background to-transparent" />
            <div className="flex justify-center">
              <PrimaryButton
                text={ad.ctaText}
                fn={() => void handleOpenChat()}
                isDisabled={isSubmitting}
                isLoading={isSubmitting}
              />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HomeAdModal;
